import { Modal, ModalOverlay, ModalContent, Box, Flex } from '@chakra-ui/react';
import { SearchFilter } from '@/components/filter';
import { useState, useRef, useEffect, useCallback } from 'react';
import { ReportCategoriesContainer } from '@/pages/reports/custom-reports/show/reportSelection/ReportCategories';
import { TitlesContainer } from '@/pages/reports/custom-reports/show/reportSelection/TilesContainer';
import { getReportCategories } from '@/pages/reports/custom-reports/show/reportSelection/CustomReportConfig';
import { CategoryItem } from '@/pages/reports/custom-reports/types/customReport';
import { KText } from '@/ui-library/components/text';
import { Button } from '@kula-ai/components';
import { useKFlags } from '@/hooks/use-k-flags';

export const AddBlockToReport = ({ modalDisclosure, onAddBlock }) => {
  const tileCompRef = useRef(null);

  const { onClose } = modalDisclosure || {};

  const flags = useKFlags();
  const reportCategories = getReportCategories({
    flags,
  });

  const [reportList, setReportList] = useState(reportCategories);
  const [hasChanges, setHasChanges] = useState(false);
  const [firstSelectedItemCategory, setFirstSelectedItemCategory] =
    useState(null);

  // Throttle ref to prevent excessive calls to detectActiveSection
  const detectActiveSectionThrottleRef = useRef(null);

  const onCategoryClick = useCallback(
    (category: CategoryItem, scrollToView = true) => {
      setReportList((prev) =>
        prev.map((cat) => ({
          ...cat,
          active: cat.id === category.id,
        }))
      );

      // Scroll to category section
      if (scrollToView && category.scrollRefID) {
        tileCompRef.current?.setScrollIntoView(category.scrollRefID);
      }
    },
    []
  );

  const handleAddBlocks = () => {
    const { selectedItems } = tileCompRef.current?.getSelectedTiles() || {
      selectedItems: [],
    };

    onAddBlock(selectedItems);
    onClose();
  };

  const handleSearchChange = (searchTerm) => {
    tileCompRef.current?.setTilesBySearch(searchTerm?.searchTerm);
  };

  const handleTilesChange = (selectedItems) => {
    setHasChanges((selectedItems?.length ?? 0) > 0);

    // Track the first selected item's category for auto-scroll
    const items = selectedItems ?? [];
    if (items.length > 0 && !firstSelectedItemCategory) {
      const firstItem = items[0];
      const itemSubjectType = firstItem?.subject_type;

      // Find the category that matches this item's subject type
      const matchingCategory = reportCategories.find((cat) => {
        // Map subject types to category IDs
        const categoryMapping = {
          pipeline: 'pipelines',
          interview: 'interviews',
          offer: 'offers',
          hire: 'hires',
          rejection: 'rejections',
          contact: 'contacts',
          job: 'jobs',
          eeo: 'eeo',
          flow_health: 'flow-health',
          flow_user_activity: 'flow-health',
          flow_candidate_touchpoint: 'flow-health',
          recruiter_performance: 'recruiter-performance',
          requisition: 'requisitions',
        };

        return cat.id === categoryMapping[itemSubjectType];
      });

      if (matchingCategory) {
        setFirstSelectedItemCategory(matchingCategory.id);

        // Small delay to ensure the selection is processed
        setTimeout(() => {
          onCategoryClick(matchingCategory, true);
        }, 100);
      }
    } else if (items.length === 0) {
      // Reset when no items are selected
      setFirstSelectedItemCategory(null);
    }
  };

  const detectActiveSection = useCallback(() => {
    const container = document.getElementById('tiles_container');
    if (!container) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    let activeCategory = null;
    let closestDistance = Infinity;

    // Check each category section and find the one closest to the top of the container
    reportCategories.forEach((category) => {
      const element = document.getElementById(category.scrollRefID);
      if (element) {
        const elementRect = element.getBoundingClientRect();
        const distanceFromTop = Math.abs(elementRect.top - containerRect.top);

        // Consider an element "active" if it's within the viewport or close to the top
        const isInViewport =
          elementRect.top <= containerRect.bottom &&
          elementRect.bottom >= containerRect.top;
        const isCloseToTop = distanceFromTop < 150; // Within 150px of container top

        if (isInViewport && isCloseToTop && distanceFromTop < closestDistance) {
          closestDistance = distanceFromTop;
          activeCategory = category;
        }
      }
    });

    // Update active category if we found one - but only if it's different from current
    if (activeCategory) {
      setReportList((prev) => {
        // Check if the active category is already set to avoid unnecessary re-renders
        const currentActive = prev.find((cat) => cat.active);
        if (currentActive?.id === activeCategory.id) {
          return prev; // Return same reference to prevent re-render
        }

        return prev.map((cat) => ({
          ...cat,
          active: cat.id === activeCategory.id,
        }));
      });
    }
  }, [reportCategories]);

  // Throttled version of detectActiveSection to prevent excessive calls
  const detectActiveSectionThrottled = useCallback(() => {
    if (detectActiveSectionThrottleRef.current) {
      clearTimeout(detectActiveSectionThrottleRef.current);
    }

    detectActiveSectionThrottleRef.current = setTimeout(() => {
      detectActiveSection();
      detectActiveSectionThrottleRef.current = null;
    }, 100); // 100ms throttle
  }, [detectActiveSection]);

  // Run initial scroll detection after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      detectActiveSection();
    }, 500);

    return () => clearTimeout(timer);
  }, [detectActiveSection]);

  const renderHeader = () => (
    <Flex
      borderBottom="1px solid"
      borderColor="border-color"
      p="20px 25px"
      justifyContent="space-between"
    >
      <Box>
        <KText size="md-semi-bold" color="secondary-900">
          Select which blocks to add
        </KText>
        <KText size="sm-regular" color="secondary-700">
          You can add one or more blocks simultaneously to your report.
        </KText>
      </Box>
      <Box>
        <SearchFilter
          searchTerm=""
          applySearch={handleSearchChange}
          inputProps={{
            width: '300px',
            fontSize: '14px',
            placeholder: 'Search blocks...',
          }}
        />
      </Box>
    </Flex>
  );

  const renderSidebar = () => (
    <Box
      p="15px 20px"
      w="200px"
      flexShrink={0}
      overflowY="auto"
      borderRight="1px solid"
      borderColor="border-color"
      data-testid="add-block-sidebar"
    >
      <ReportCategoriesContainer
        categories={reportList}
        categoryOnClick={(category) => onCategoryClick(category, true)}
      />
    </Box>
  );

  const renderContent = () => (
    <Flex flexDir="column" width="100%">
      <Box width="100%" flex={1} overflow="auto" id="tiles_container">
        <TitlesContainer
          report={{ tiles: [] }}
          ref={tileCompRef}
          onChange={handleTilesChange}
          onScroll={() => {
            detectActiveSectionThrottled();
          }}
        />
      </Box>
      {renderFooter()}
    </Flex>
  );

  const renderFooter = () => (
    <Flex
      h="58px"
      justifyContent="flex-end"
      p="12px 20px"
      borderTop="1px solid"
      borderColor="border-color"
    >
      <Flex alignItems="center" gap="6px">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!hasChanges}
          onClick={handleAddBlocks}
        >
          Add blocks
        </Button>
      </Flex>
    </Flex>
  );

  return (
    <Modal isOpen onClose={onClose} size="6xl" autoFocus={false} isCentered>
      <ModalOverlay />
      <ModalContent minH="80%">
        <Box
          display="flex"
          flex={1}
          py="0"
          maxH="80vh"
          minH="400px"
          flexDirection="column"
        >
          {renderHeader()}
          <Flex overflow="hidden" flex={1}>
            {renderSidebar()}
            {renderContent()}
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
};
